import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Box, Button, CircularProgress, Divider } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  H1,
  H3,
  ActiveFilterChips,
  colorsLight,
  NotFoundPage,
  SelectOptionsHook,
  useLazyLoadingQueryOptionsHook,
  DropdownOption,
} from "@coherehealth/common";
import {
  AdvancedSearchServiceCaseRequest,
  useGetReferenceHealthPlans,
  useGetReferenceDelegatedVendors,
  useGetReferenceCaseTypes,
  useGetReferenceCaseStatuses,
  useGetReferenceOutcomes,
  useSearchUser,
  useUserQmProfile,
  User,
} from "@coherehealth/qm-api";
import { useSnackbar } from "notistack";
import { useAuthorized } from "Authorization";
import { useLaunchDarklyFeatureFlag } from "hooks/useLaunchDarklyFeatureFlag";
import { useFindACaseLdContext } from "hooks/useFindACaseLdContext";
import useUserRoles from "hooks/useUserRoles";
import NoResultsClipboardIcon from "components/images/NoResultsClipboardIcon";
import SearchPanel from "./SearchPanel";
import SearchResultsTable from "./SearchResultsTable";
import { SEARCH_PAGE_SIZE } from "./constants";
import { useSearchFormState } from "./useSearchFormState";
import { useSearchFilterConfigs, SearchFilterDeps } from "./useSearchFilterConfigs";
import { useSearchResults } from "./useSearchResults";
import { FieldId, UserCtx } from "./fieldRegistry";
import { deduplicateByValue, filterOutcomesByCaseTypes } from "./outcomeFilterMap";
import { useOutcomeAutoRemoval } from "./useOutcomeAutoRemoval";

// Module-scope: stable hook identity so FilterDropdownWithSearch never resets mid-interaction.
// Returns DropdownOption[] (id = name) so FilterDropdownWithSearch's existing partition/groupBy
// logic works without changes — selectedValues are name strings and option.id is also the name.
const useAssigneeFilterOptions: SelectOptionsHook<DropdownOption> = (selectOptionsParams) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const result = useLazyLoadingQueryOptionsHook<User, unknown>({
    // Cast selectOptionsParams: its onError/query/offset/max fields are structurally identical
    // to SelectOptionsHookParams<User> — only the generic differs.
    ...(selectOptionsParams as unknown as Parameters<SelectOptionsHook<User>>[0]),
    useGetHook: useSearchUser,
    additionalQueryParams: { name: selectOptionsParams.query, supportOnly: false },
  });
  return {
    optionsLoading: result.optionsLoading,
    hasMoreOptions: result.hasMoreOptions,
    refetch: result.refetch as ReturnType<SelectOptionsHook<DropdownOption>>["refetch"],
    options: (result.options || []).filter((u) => u?.name).map((u) => ({ id: u.name!, label: u.name! })),
  };
};

/**
 * "Find a case" page: shell + inline search form. Visibility is controlled by the
 * LaunchDarkly flag `qmFindACase` (evaluated against the current user's context); the
 * FIND_A_CASE permission is a broad reachability ceiling, not the live gate.
 */
export default function FindACase() {
  const canAccessPage = useAuthorized("FIND_A_CASE");
  const isSupervisor = useAuthorized("SUPERVISOR");
  const context = useFindACaseLdContext();
  const { featureFlagEnabled, isLoading } = useLaunchDarklyFeatureFlag({
    featureFlagName: "qmFindACase",
    context,
  });

  const { data: profile } = useUserQmProfile({});
  const roles = useUserRoles();
  const { enqueueSnackbar } = useSnackbar();

  const { data: healthPlans, error: hpError } = useGetReferenceHealthPlans({});
  const { data: caseTypes, error: ctError } = useGetReferenceCaseTypes({});
  const { data: caseStatuses, error: csError } = useGetReferenceCaseStatuses({});
  const { data: outcomes, error: outError } = useGetReferenceOutcomes({});
  const userCtx: UserCtx = useMemo(() => {
    // false while healthPlans is loading (undefined); safe because SearchResultsTable is only
    // mounted after hasSearched=true, which requires a user-triggered search after load completes.
    const hasCrossClientAccess = (healthPlans ?? []).length > 1;
    return {
      hasCrossClientAccess,
      // Delegated Vendor options are derived from the selected health plan, so the field is only
      // meaningful (and shown) when the Health Plan field is shown.
      hasDelegatedVendor: hasCrossClientAccess,
      isSupervisor,
      roles: roles || [],
      opsGroup: profile?.opsGroup,
    };
  }, [healthPlans, isSupervisor, profile?.opsGroup, roles]);

  const healthPlanLabels = useMemo(
    () =>
      (healthPlans ?? []).reduce<Record<string, string>>((acc, hp) => {
        if (hp.value && hp.label) {
          acc[hp.value] = hp.label;
        }
        return acc;
      }, {}),
    [healthPlans]
  );

  // TODO: sync filter values, sort state, and page to URL search params so the back button
  // restores the previous search and results are bookmarkable.
  const { formValues, setField, resetField, resetAll: resetFormValues } = useSearchFormState();

  const selectedHealthPlanId = (formValues.healthPlan as DropdownOption | null | undefined)?.id;
  const {
    data: delegatedVendors,
    error: dvError,
    loading: dvLoading,
  } = useGetReferenceDelegatedVendors({
    queryParams: { healthPlan: selectedHealthPlanId },
    lazy: !selectedHealthPlanId,
  });
  // restful-react keeps the previous plan's data while a new request is in flight; never show
  // another plan's vendors, and show none when no plan is selected (the request is lazy then).
  const delegatedVendorOptions = useMemo(
    () => (selectedHealthPlanId && !dvLoading ? delegatedVendors ?? [] : []),
    [selectedHealthPlanId, dvLoading, delegatedVendors]
  );

  const selectedCaseTypeIds = useMemo(
    () => (formValues.caseType as DropdownOption[] | undefined)?.map((o) => o.id) ?? [],
    [formValues.caseType]
  );

  const deduplicatedOutcomes = useMemo(() => deduplicateByValue(outcomes ?? []), [outcomes]);

  const filteredOutcomes = useMemo(
    () => filterOutcomesByCaseTypes(deduplicatedOutcomes, selectedCaseTypeIds),
    [deduplicatedOutcomes, selectedCaseTypeIds]
  );

  useOutcomeAutoRemoval(selectedCaseTypeIds, filteredOutcomes, formValues, setField);

  // True when every field is at its initial empty value ([] or null).
  // Used to auto-clear results when the user removes all filters — catches every clear
  // path: chip X (collapsed panel), dropdown X (expanded panel), resetAll, and
  // resetWhen cascades (e.g. clearing caseStatus also empties outcome).
  // Note: no `v === ""` branch needed — useSearchFormState initialises all field types
  // (including TextFields) as [] not "", and SearchPanel stores text values as [value].
  const isFormEmpty = useMemo(
    () => Object.values(formValues).every((v) => v === null || v === undefined || (Array.isArray(v) && v.length === 0)),
    [formValues]
  );

  const { results, totalCount, loading: searchLoading, search, clearResults, updateResult } = useSearchResults();
  const [hasSearched, setHasSearched] = useState(false);
  const [expanded, setExpanded] = useState(true);
  // useRef (not useState): handleSearchParamsChange must always read the latest request without
  // being in a dep chain. useState schedules a re-render; if handleSortChange fires in the
  // same tick as handleSearch, handleSearchParamsChange could still hold the previous value.
  const lastSearchRequestRef = useRef<AdvancedSearchServiceCaseRequest | null>(null);
  const [searchVersion, setSearchVersion] = useState(0);

  const refDataError = hpError || dvError || ctError || csError || outError;
  useEffect(() => {
    if (refDataError) {
      enqueueSnackbar("Failed to load filter options. Please refresh the page.", {
        variant: "error",
        preventDuplicate: true,
      });
    }
  }, [refDataError, enqueueSnackbar]);

  // When every filter field is empty and there are stale results showing, clear them.
  // Fires for all removal paths: chip X, dropdown X, resetAll, and resetWhen cascades.
  // Debounced 300ms: text fields pass through [] while the user retypes a value (e.g.
  // selecting "12345" and typing "12346"), which would falsely trigger an immediate clear.
  // The delay means a deliberate "clear all" (no new typing within 300ms) still clears.
  const clearDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (clearDebounceRef.current) {
      clearTimeout(clearDebounceRef.current);
      clearDebounceRef.current = null;
    }
    if (isFormEmpty && hasSearched) {
      clearDebounceRef.current = setTimeout(() => {
        clearResults();
        setHasSearched(false);
        lastSearchRequestRef.current = null;
      }, 300);
    }
    return () => {
      if (clearDebounceRef.current) {
        clearTimeout(clearDebounceRef.current);
      }
    };
  }, [isFormEmpty, hasSearched, clearResults]);

  const deps: SearchFilterDeps = useMemo(
    () => ({
      healthPlanOptions: healthPlans ?? [],
      delegatedVendorOptions,
      caseTypeOptions: caseTypes ?? [],
      caseStatusOptions: caseStatuses ?? [],
      outcomeOptions: filteredOutcomes,
      useAssigneeOptions: useAssigneeFilterOptions,
    }),
    [healthPlans, delegatedVendorOptions, caseTypes, caseStatuses, filteredOutcomes]
  );

  const resolvedFields = useSearchFilterConfigs(deps, formValues, userCtx);
  const filterConfigs = useMemo(() => resolvedFields.map((rf) => rf.filterConfig), [resolvedFields]);

  const handleSearch = useCallback(
    (_flatParams: Record<string, unknown>, apiRequest: AdvancedSearchServiceCaseRequest) => {
      // Store apiRequest WITHOUT max/page: buildSearchRequest never sets those fields,
      // so handleSearchParamsChange's ...params spread cleanly provides the correct values.
      lastSearchRequestRef.current = apiRequest;
      setHasSearched(true);
      setSearchVersion((v) => v + 1);
      search({ ...apiRequest, max: SEARCH_PAGE_SIZE, page: 0 });
    },
    [search]
  );

  const handleSearchParamsChange = useCallback(
    (params: { page: number; max: number; sortBy?: string[]; sortDesc?: boolean }) => {
      if (lastSearchRequestRef.current) {
        search({ ...lastSearchRequestRef.current, ...params });
      }
    },
    [search] // lastSearchRequestRef is a ref: stable identity, always reads current value
  );

  const resetAll = useCallback(() => {
    resetFormValues();
    clearResults();
    setHasSearched(false);
    lastSearchRequestRef.current = null;
    setSearchVersion(0); // reset so the next search always increments cleanly from 0→1
  }, [resetFormValues, clearResults]);

  const handleClearFilter = useCallback(
    (key: string) => {
      resetField(key as FieldId);
      // Result clearing is handled by the isFormEmpty effect above, which catches
      // this path and all other clear paths (dropdown X, resetAll, cascades).
    },
    [resetField]
  );

  // context is undefined until useFindACaseLdContext resolves the user profile. The LD hook
  // skips evaluation while context is undefined (keeping isLoading=true), but we also guard
  // here explicitly so we never reach the <NotFoundPage /> branch before the flag is known.
  // roles === undefined means useUserRoles() hasn't resolved yet; useAuthorized returns false
  // while loading, which would cause a NotFoundPage flash before canAccessPage becomes true.
  if (isLoading || context === undefined || roles === undefined) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress />
      </Box>
    );
  }

  // The flag is the live gate; the permission is a defense-in-depth floor. A direct-URL hit with
  // the flag off (or by a non-permitted user) resolves to Not Found rather than an empty shell.
  if (!featureFlagEnabled || !canAccessPage) {
    return <NotFoundPage />;
  }

  return (
    <div style={{ backgroundColor: colorsLight.background.mid, minHeight: "100vh" }}>
      <div
        style={{
          backgroundColor: "white",
          paddingLeft: 32,
          paddingRight: 32,
          paddingTop: 40,
          paddingBottom: expanded ? 40 : 16,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: expanded ? 32 : 12,
          }}
        >
          <H1>Find a case</H1>
          <Button
            variant="text"
            onClick={() => setExpanded(!expanded)}
            endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            aria-expanded={expanded}
            aria-controls="find-a-case-search-panel"
            aria-label={expanded ? "Collapse search filters" : "Expand search filters"}
            data-testid={expanded ? "collapse-button" : "expand-button"}
          >
            {expanded ? "Collapse" : "Expand"}
          </Button>
        </div>

        {/* Keep the panel div always mounted so aria-controls on the button always resolves to a real element */}
        <div id="find-a-case-search-panel" hidden={!expanded}>
          <SearchPanel
            formValues={formValues}
            setField={setField}
            resetAll={resetAll}
            onSearch={handleSearch}
            resolvedFields={resolvedFields}
            filterConfigs={filterConfigs}
            isSearching={searchLoading}
          />
        </div>
        {!expanded && (
          // No marginLeft wrapper here: Figma collapsed-state frame shows chips flush-left
          // at x=0 within the card content area (paddingLeft:32 on the card provides the indent).
          // FiltersPopover adds its own marginLeft:8 wrapper internally to space chips from the
          // filter button, but that pattern does not apply to this direct usage.
          <ActiveFilterChips
            filterConfigs={filterConfigs}
            filterValues={formValues as Record<string, unknown>}
            onClearAll={resetAll}
            onClearFilter={handleClearFilter}
          />
        )}
      </div>
      <Divider />
      <div style={{ paddingTop: 20, paddingLeft: 28, paddingRight: 30, paddingBottom: 32 }}>
        {!hasSearched ? (
          <div
            data-testid="find-a-case-empty-state"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 80,
              padding: "120px 80px",
              border: `1px solid ${colorsLight.gray.divider}`,
              borderRadius: 8,
            }}
          >
            <NoResultsClipboardIcon />
            <div style={{ display: "flex", flexDirection: "column", gap: 16, opacity: 0.75 }}>
              <H1 style={{ color: colorsLight.font.light }}>No results yet.</H1>
              <H3 style={{ color: colorsLight.font.light }}>Fill in one or more fields to search for a case</H3>
            </div>
          </div>
        ) : (
          <SearchResultsTable
            results={results}
            totalCount={totalCount}
            loading={searchLoading}
            userCtx={userCtx}
            healthPlanLabels={healthPlanLabels}
            searchVersion={searchVersion}
            canReassign={isSupervisor}
            onSearchParamsChange={handleSearchParamsChange}
            onAssigneeUpdated={updateResult}
          />
        )}
      </div>
    </div>
  );
}
